// Website pixels: the detail drawer (identity, provider integrations, server
// key, funnel defaults, install snippet, activity) and the show-once
// server-key modal. Opens the Pixel Studio for extraction, identity and the
// live tester. The save itself lives in the parent (pixels.tsx), which passes
// down the sentence saying what the last save did (`notice`): the drawer stays
// open after a save, as the Users drawer does.

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { Icons, fmt } from '@/components/primitives';
import { SearchableDropdown } from '@/components/dropdowns';
import type { SearchableDropdownOption } from '@/components/dropdowns';
import { useProductions } from '@/api/hooks';
import { pixelsList } from '@/api/resources';
import { INSTALL_PARAMS, PIXEL_FANOUT_CAP } from '@/api/types.gen';
import type {
  PixelApiValue,
  PixelConversions,
  PixelDefaults,
  PixelServerKeyResponse,
  PixelShareLinkResponse,
  PixelStatus,
} from '@/api/types';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { queryClient } from '@/cache/queryClient';
import { MutationErrorLine } from '@/components/error-panel';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { fmtDate } from '@/lib/datetime';
import {
  PIXEL_EVENTS,
  domainLabel,
  isValidEncoreId,
  pixelDraftRequiredIssues,
  toWireDomain,
} from '@/lib/pixel-draft';
import type { PixelActivityByEvent, PixelDraft } from '@/lib/pixel-draft';
import { fmtRelative } from './admin-user-drawer';
import { InstallSnippetBuilder } from './pixel-install';
import {
  COMMON_MISTAKES,
  CoveragePills,
  LockIcon,
  LockToggleButton,
  LockedField,
  LockedValue,
  ParamRefTable,
} from './pixel-parts';
import { PixelStudio } from './pixel-studio';

// Prefix-invalidate every `pixels.list` variant after a write. Read off the
// resource: a rename must not silently turn this into a no-op.
const PIXELS_LIST_PREFIX = pixelsList.all;

export interface PixelDetailDrawerProps {
  pixel: PixelApiValue;
  activity: PixelActivityByEvent | undefined;
  activityIssue: boolean;
  onClose: () => void;
  onSave?: (before: PixelApiValue, draft: PixelDraft) => void;
  /** What the last save did ("Saved."), shown at the top of the drawer. */
  notice?: string | null;
  /** When a save last returned `pixel` (0 if none): the draft resets to it. */
  savedAt?: number;
  /** The operator changed the draft: the last save's sentence no longer holds. */
  onEdit?: () => void;
  onArchive?: (pixel: PixelApiValue) => void;
  onDuplicate?: (id: string, newId: string) => void;
  busy: boolean;
  actionError: Error | null;
  canManage: boolean;
  providerDefaults: PixelDefaults | undefined;
}

export function PixelDetailDrawer({
  pixel, activity, activityIssue, onClose, onSave, notice = null, savedAt = 0, onEdit,
  onArchive, onDuplicate, busy, actionError, canManage, providerDefaults,
}: PixelDetailDrawerProps) {
  const [draft, setDraft] = useState<PixelDraft>(pixel);
  const [providerLocked, setProviderLocked] = useState(true);
  const [funnelLocked,   setFunnelLocked]   = useState(true);
  const [domainInput, setDomainInput] = useState('');
  const [dupOpen, setDupOpen] = useState(false);
  const [dupId, setDupId] = useState('');
  const duplicateRequired = useRequiredFeedback();
  const pixelRequired = useRequiredFeedback();
  const [studioOpen, setStudioOpen] = useState(false);
  // Productions catalog — feeds the funnel-default production picker. Labels
  // carry the EncorePro house code so operators can search by either.
  const productionsQ = useProductions();
  const productionOptions = useMemo((): SearchableDropdownOption[] => (productionsQ.data ?? []).map(p => {
    const ref = (p.source_reference || '').trim();
    const hasRef = ref && ref !== '<unknown>';
    return { id: p.id, label: hasRef ? `${p.name} [${ref}]` : p.name };
  }), [productionsQ.data]);
  useEffect(() => {
    setDraft(pixel);
    setProviderLocked(true);
    setFunnelLocked(true);
    setDomainInput('');
    setDupOpen(false);
    setDupId('');
    duplicateRequired.clearAll();
    pixelRequired.clearAll();
    setStudioOpen(false);
    setMintedKey(null);
  }, [pixel.id]);
  // A save landed: the drawer shows the pixel the server returned, locked again
  // as on open, and leaves the Studio so the "Saved." line is in view.
  useEffect(() => {
    if (!savedAt) return;
    setDraft(pixel);
    setProviderLocked(true);
    setFunnelLocked(true);
    pixelRequired.clearAll();
    setStudioOpen(false);
  }, [savedAt]);
  // No early return: `pixel` is required (the list renders the drawer only with
  // a row), so every hook below runs on every render, in the same order.
  const set = (patch: Partial<PixelDraft>) => {
    setDraft(d => ({ ...d, ...patch }));
    onEdit?.();
  };
  const [linkCopied, setLinkCopied] = useState(false);
  // Real activity summary (from usePixelActivity, per selected pixel) — feeds
  // the header badge and the Status line so neither claims anything the live
  // counts don't support.
  const events24 = PIXEL_EVENTS.reduce((a, ev) => a + (activity?.[ev]?.count_24h ?? 0), 0);
  const events1h = PIXEL_EVENTS.reduce((a, ev) => a + (activity?.[ev]?.count_1h ?? 0), 0);
  const events7d = PIXEL_EVENTS.reduce((a, ev) => a + (activity?.[ev]?.count_7d ?? 0), 0);
  const lastAnyAt = PIXEL_EVENTS
    .map(ev => activity?.[ev]?.last_event_at)
    .filter(Boolean)
    .sort()
    .pop();
  // Header badge: label + color for every PixelStatus (active | paused | archived).
  const STATUS_BADGE: Record<PixelStatus, { label: string; color: string }> = {
    active:   { label: 'ACTIVE',   color: 'var(--good)'  },
    paused:   { label: 'PAUSED',   color: 'var(--warn)'  },
    archived: { label: 'ARCHIVED', color: 'var(--ink-3)' },
  };
  const statusBadge = STATUS_BADGE[pixel.status] || STATUS_BADGE.archived;
  // Honest Status line: lifecycle first, then a graded phrase derived from the
  // real activity feed (not the stored enum).
  const statusLine = () => {
    if (pixel.status === 'archived') return 'Archived · no longer firing';
    if (pixel.status === 'paused')   return 'Paused · not firing';
    if (activityIssue)               return 'Active · activity status unavailable';
    if (events1h > 0)                return 'Active · receiving events';
    if (events7d > 0 && lastAnyAt)   return `Active · last event ${fmtRelative(+new Date(lastAnyAt))}`;
    return 'Active · no recent events';
  };
  const shareM = useRegisteredMutation({ domains: ['pixels'], mutationFn: (_: void) => api.post<PixelShareLinkResponse>(`/pixels/${encodeURIComponent(draft.id)}/share-link`, { body: {} }) });
  const revokeM = useRegisteredMutation({ domains: ['pixels'], mutationFn: (_: void) => api.post<unknown>(`/pixels/${encodeURIComponent(draft.id)}/revoke-share`, { body: {} }) });
  const [shareInfo, setShareInfo] = useState<{ url: string; expires_at: string } | null>(null);
  const onCopyLink = async () => {
    try {
      const res = await shareM.mutateAsync();
      const url = res?.url;
      const expires_at = res?.expires_at;
      if (url) { navigator.clipboard?.writeText(url); setShareInfo({ url, expires_at }); setLinkCopied(true); setTimeout(()=>setLinkCopied(false), 2200); }
    } catch (e) { /* surfaced via shareM.error */ }
  };
  const onRevoke = async () => { try { await revokeM.mutateAsync(); setShareInfo(null); } catch (e) { /* shown via revokeM.error */ } };

  // Server key (sGTM) — mint/rotate + revoke, same mutation idiom as the share
  // link above. The key is returned ONCE by the mint response; we surface it in
  // a show-once modal and never fetch it again (the registry only stores a hash).
  const mintKeyM = useRegisteredMutation({ domains: ['pixels'], mutationFn: (_: void) => api.post<PixelServerKeyResponse>(`/pixels/${encodeURIComponent(draft.id)}/server-key`, { body: {} }) });
  const revokeKeyM = useRegisteredMutation({ domains: ['pixels'], mutationFn: (_: void) => api.post<unknown>(`/pixels/${encodeURIComponent(draft.id)}/server-key/revoke`, { body: {} }) });
  const [mintedKey, setMintedKey] = useState<string | null>(null);
  const invalidatePixelsList = () => queryClient.invalidateQueries({ queryKey: PIXELS_LIST_PREFIX });
  const onMintKey = async () => {
    try {
      const res = await mintKeyM.mutateAsync();
      const key = res?.server_key;
      if (key) setMintedKey(key);
      invalidatePixelsList();
    } catch (e) { /* surfaced via mintKeyM.error */ }
  };
  const onRevokeKey = async () => {
    try {
      await revokeKeyM.mutateAsync();
      setMintedKey(null);
      invalidatePixelsList();
    } catch (e) { /* surfaced via revokeKeyM.error */ }
  };
  // Server-owned fields are read from the row, never from the draft. The draft
  // is a copy taken when the drawer opened and reset only for a new pixel id,
  // while Mint / Rotate / Revoke change these on the server and refetch the
  // list, which updates `pixel` under the same id. Read off the draft, the
  // drawer kept offering "Mint key" after a mint until it was reopened.
  // `?? defaults` keep the drawer working against an older API.
  const hasServerKey = !!(pixel.has_server_key ?? false);
  const serverKeySetAt = pixel.server_key_set_at ?? null;
  // The snippet builder's sGTM guide reads the key state too.
  const snippetPixel: PixelDraft = {
    ...draft,
    has_server_key: pixel.has_server_key,
    server_key_set_at: pixel.server_key_set_at,
  };
  const fmtKeyDate = (v: number | string | null | undefined) => {
    if (v == null || v === '') return '';
    const ms = typeof v === 'number' || /^\d+$/.test(String(v)) ? Number(v) : Date.parse(v);
    return isNaN(ms) ? String(v) : fmtDate(ms);
  };

  // Server conversions config (PixelConversions) — default object when the
  // registry row predates the column (null/undefined).
  const conv: PixelConversions = draft.conversions ?? { meta_capi: false, ttd: false, test_event_code: null, browser_ttd_beacon: null };
  const setConv = (patch: Partial<PixelConversions>) => set({ conversions: { ...conv, ...patch } });

  const metaOn = !!draft.meta_pixel_id || draft._metaOn;
  const ttdOn  = !!draft.ttd_adv_id || draft._ttdOn;
  const funnelOn = !!(draft.default_venue_id || draft.default_production_id) || draft._funnelOn;

  // Provider enablement is transient until an ID is entered. There is no
  // client-side org default, so enabling an unconfigured provider reveals
  // blank fields without changing any configured IDs.
  const toggleMeta = () => {
    if (providerLocked) return;
    pixelRequired.clear('pixel-platform');
    pixelRequired.clear('pixel-meta-id');
    if (metaOn) {
      set({ _metaOn: false, meta_pixel_id: null });
    } else {
      set({ _metaOn: true, meta_pixel_id: providerDefaults?.meta_pixel_id ?? null });
    }
  };
  const toggleTtd = () => {
    if (providerLocked) return;
    pixelRequired.clear('pixel-platform');
    pixelRequired.clear('pixel-ttd-advertiser');
    pixelRequired.clear('pixel-ttd-conversion');
    pixelRequired.clear('pixel-ttd-pageview');
    if (ttdOn) {
      set({ _ttdOn: false, ttd_adv_id: null, ttd_ct_id: null, ttd_ct_id_pageview: null });
    } else {
      set({
        _ttdOn: true,
        ttd_adv_id: providerDefaults?.ttd_adv_id ?? null,
        ttd_ct_id: providerDefaults?.ttd_ct_id ?? null,
        ttd_ct_id_pageview: providerDefaults?.ttd_ct_id_pageview ?? null,
      });
    }
  };
  const toggleFunnel = () => set({ _funnelOn: !funnelOn, default_venue_id: funnelOn ? null : (draft.default_venue_id || ''), default_production_id: funnelOn ? null : (draft.default_production_id || '') });

  const addDomain = (raw: string) => {
    const wire = toWireDomain(raw);
    if (!wire) return;
    const existing = draft.domains || [];
    if (existing.includes(wire)) return;
    set({ domains: [...existing, wire] });
    pixelRequired.clear('pixel-domains');
  };
  const attemptSave = (fromStudio = false) => {
    if (busy) return;
    const issues = pixelDraftRequiredIssues(draft);
    const first = issues[0];
    const firstIsStudio = first?.key.startsWith('pixel-mapping-rule-')
      || first?.key.startsWith('pixel-pii-rule-')
      || first?.key === 'pixel-consent-var';
    const firstIsProvider = first?.key.startsWith('pixel-meta-')
      || first?.key.startsWith('pixel-ttd-')
      || first?.key === 'pixel-test-event-code';
    const firstIsFunnel = first?.key === 'pixel-default-venue'
      || first?.key === 'pixel-default-production';
    if (firstIsProvider) setProviderLocked(false);
    if (firstIsFunnel) setFunnelLocked(false);
    if (firstIsStudio) setStudioOpen(true);
    else if (fromStudio && first) setStudioOpen(false);
    if (!pixelRequired.report(issues)) return;
    onSave?.(pixel, draft);
  };
  const submitDuplicate = () => {
    if (busy) return;
    const valid = duplicateRequired.report([
      dupId.length < 3 && {
        key: 'duplicate-pixel-id',
        message: 'Enter a new pixel ID — at least 3 characters.',
      },
    ]);
    if (!valid) return;
    onDuplicate?.(pixel.id, dupId);
  };

  return (
    <>

      <ResponsiveOverlay label="pixels details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{flex:1, minWidth:0}}>
            <div className="muted mono" style={{fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>Pixel · <span style={{color:'var(--ink-1)'}}>{pixel.id}</span></div>
            <h2 style={{fontFamily:'var(--font-display)',fontSize:20,fontWeight:800,letterSpacing:'.01em',margin:'4px 0 6px'}}>{draft.description || pixel.id}</h2>
            <div className="flex items-center gap-2">
              <span className="tag" style={{
                borderColor: statusBadge.color,
                color: statusBadge.color,
                background:'transparent',fontWeight:700,
              }}>
                <span className="dot" style={{background: statusBadge.color}}/>
                {statusBadge.label}
              </span>
              <CoveragePills pixel={draft}/>
              <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em'}}>
                {activityIssue ? '— events / 24h' : `${fmt.n(events24)} event${events24 === 1 ? '' : 's'} / 24h`}
              </span>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          {/* Result of the last save, as the Users drawer shows it. The live
              region is always mounted so a screen reader announces the
              sentence when it appears. */}
          <div role="status">
            {notice && (
              <div style={{
                padding:'10px 12px', marginBottom:14,
                background:'color-mix(in oklch, var(--good) 12%, transparent)',
                border:'1px solid color-mix(in oklch, var(--good) 40%, var(--line))',
                borderLeft:'3px solid var(--good)',
                borderRadius:4,
                display:'flex', alignItems:'center', gap:10,
                fontSize:11.5, color:'var(--ink-1)',
              }}>
                <span style={{color:'var(--good)'}}>{Icons.check}</span>
                <span>{notice}</span>
              </div>
            )}
          </div>
          <div className="field-lbl mb-2">Identity</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div className="field" style={{marginBottom:10}}>
              <label className="field-lbl">
                <span>ID <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>TOML key · case-sensitive</span></span>
              </label>
              <LockedValue value={draft.id} placeholder="—"/>
              <div className="field-hint">
                IDs are immutable — every deployed snippet references it. Use <b>Duplicate</b> below to fork under a new ID.
              </div>
            </div>
            <div className="field" style={{marginBottom:10}}>
              <label className="field-lbl">Description</label>
              <input className="input" aria-label="Description" value={draft.description || ''} onChange={e=>set({description: e.target.value})}/>
            </div>
            <RequiredField
              fieldKey="pixel-owner"
              message={pixelRequired.messages['pixel-owner']}
              attempt={pixelRequired.attempt}
              className="field"
              style={{marginBottom:10}}
            >
              <label className="field-lbl">Owner</label>
              <input className="input mono" style={{fontSize:12}} value={draft.owner_email || ''} onChange={e=>{
                const next = e.target.value;
                set({owner_email: next});
                if (/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(next.trim())) {
                  pixelRequired.clear('pixel-owner');
                }
              }}/>
            </RequiredField>
            <RequiredField
              fieldKey="pixel-tenant"
              message={pixelRequired.messages['pixel-tenant']}
              attempt={pixelRequired.attempt}
              className="field"
              style={{marginBottom:10}}
            >
              <label className="field-lbl">Tenant <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>lowercase slug</span></label>
              <input className="input mono" style={{fontSize:12}} value={draft.tenant_id || ''} onChange={e=>{
                const next = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g,'_');
                set({tenant_id: next});
                if (/^[a-z0-9_-]{1,64}$/.test(next)) pixelRequired.clear('pixel-tenant');
              }}/>
            </RequiredField>
            <RequiredField
              fieldKey="pixel-deployed-at"
              message={pixelRequired.messages['pixel-deployed-at']}
              attempt={pixelRequired.attempt}
              className="field"
              style={{marginBottom:10}}
            >
              <label className="field-lbl">Deployed at</label>
              <input
                type="date"
                className="input mono"
                style={{fontSize:12}}
                value={draft.deployed_at || ''}
                onChange={e=>{
                  set({deployed_at: e.target.value});
                  if (e.target.value) pixelRequired.clear('pixel-deployed-at');
                }}
              />
            </RequiredField>
            <RequiredField
              fieldKey="pixel-domains"
              message={pixelRequired.messages['pixel-domains']}
              attempt={pixelRequired.attempt}
              className="field"
            >
              <label className="field-lbl">Domains</label>
              <div className="flex" style={{flexWrap:'wrap',gap:6,marginBottom:6}}>
                {(draft.domains || []).map((d,i) => (
                  <span key={i} className="tag" title={d} style={{fontFamily:'var(--font-mono)',fontSize:11,letterSpacing:0,textTransform:'none',padding:'3px 8px'}}>
                    {domainLabel(d)}
                    <span className="x" style={{marginLeft:6,cursor:'pointer'}} onClick={()=>set({domains: draft.domains.filter((_,j)=>j!==i)})}>×</span>
                  </span>
                ))}
              </div>
              <input
                className="input mono"
                style={{fontSize:12}}
                placeholder="test.com or https://us.test.com — both normalize to the root domain"
                value={domainInput}
                onChange={e => setDomainInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                    addDomain(e.currentTarget.value);
                    setDomainInput('');
                  }
                }}
                onBlur={e => {
                  if (e.currentTarget.value.trim()) {
                    addDomain(e.currentTarget.value);
                    setDomainInput('');
                  }
                }}
              />
              {domainInput.trim() && toWireDomain(domainInput) && toWireDomain(domainInput) !== domainInput.trim() && (
                <div className="field-hint">Will be saved as <code style={{fontFamily:'var(--font-mono)'}}>{toWireDomain(domainInput)}</code> (covers all subdomains)</div>
              )}
            </RequiredField>
          </div>

          <RequiredField
            fieldKey="pixel-platform"
            message={pixelRequired.messages['pixel-platform']}
            attempt={pixelRequired.attempt}
            surface
          >
            <div className="field-lbl mb-2 flex between items-center" style={{paddingRight:2}}>
              <span>Provider integrations</span>
              <LockToggleButton locked={providerLocked} onClick={() => setProviderLocked(l => !l)}/>
            </div>
            <div className="panel" style={{padding:12,marginBottom:14}}>
            {providerLocked && (
              <div style={{
                display:'flex',alignItems:'center',gap:8,
                padding:'6px 10px',marginBottom:12,
                background:'var(--bg-sunk)',
                border:'1px solid var(--line)',
                borderRadius:'var(--r-sm,4px)',
                fontSize:11.5,color:'var(--ink-2)',
              }}>
                <span style={{color:'var(--ink-3)'}}><LockIcon/></span>
                <span>Provider IDs are locked to prevent accidental edits. Click <b>Edit</b> above to change them.</span>
              </div>
            )}

            {/* Meta */}
            <div className="flex between items-center" style={{marginBottom: metaOn ? 10 : 0}}>
              <div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:13,letterSpacing:'.02em'}}>Meta Pixel</div>
                <div className="muted" style={{fontSize:11}}>Facebook / Instagram conversion events</div>
              </div>
              <button
                className="btn btn-sm"
                onClick={toggleMeta}
                disabled={providerLocked}
                style={metaOn
                  ? {background:'var(--good)',color:'#fff',borderColor:'var(--good)',opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}
                  : {opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}}
              >{metaOn ? 'ON' : 'OFF'}</button>
            </div>
            {metaOn && (
              <RequiredField
                fieldKey="pixel-meta-id"
                message={pixelRequired.messages['pixel-meta-id']}
                attempt={pixelRequired.attempt}
                style={{marginTop:8}}
              >
                {providerLocked ? (
                  <LockedField label="Meta Pixel ID" value={draft.meta_pixel_id} placeholder="—"/>
                ) : (
                  <div className="field">
                    <label className="field-lbl">Meta Pixel ID</label>
                    <input className="input mono" style={{fontSize:12}} placeholder="16-digit pixel ID" value={draft.meta_pixel_id || ''} onChange={e=>{
                      set({meta_pixel_id: e.target.value});
                      if (/^\d{10,20}$/.test(e.target.value.trim())) pixelRequired.clear('pixel-meta-id');
                    }}/>
                    <div className="field-hint">{providerDefaults?.meta_pixel_id ? <>Organization default: <code>{providerDefaults.meta_pixel_id}</code></> : 'No organization default is configured for this ID.'}</div>
                  </div>
                )}
              </RequiredField>
            )}

            <div style={{height:1,background:'var(--line-2)',margin:'14px 0'}}/>

            {/* TTD */}
            <div className="flex between items-center" style={{marginBottom: ttdOn ? 10 : 0}}>
              <div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:13,letterSpacing:'.02em'}}>CTV / DISPLAY</div>
                <div className="muted" style={{fontSize:11}}>Display / CTV conversion + pageview tags</div>
              </div>
              <button
                className="btn btn-sm"
                onClick={toggleTtd}
                disabled={providerLocked}
                style={ttdOn
                  ? {background:'var(--good)',color:'#fff',borderColor:'var(--good)',opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}
                  : {opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}}
              >{ttdOn ? 'ON' : 'OFF'}</button>
            </div>
            {ttdOn && (
              <div style={{marginTop:8}}>
                {providerLocked ? (
                  <>
                    <LockedField label="Advertiser ID"               value={draft.ttd_adv_id}         placeholder="—"/>
                    <LockedField label="Conversion Tag ID"           value={draft.ttd_ct_id}          placeholder="—"/>
                    <LockedField label="Pageview Conversion Tag ID"  value={draft.ttd_ct_id_pageview} placeholder="—"/>
                  </>
                ) : (
                  <>
                    <RequiredField
                      fieldKey="pixel-ttd-advertiser"
                      message={pixelRequired.messages['pixel-ttd-advertiser']}
                      attempt={pixelRequired.attempt}
                      className="field"
                      style={{marginBottom:8}}
                    >
                      <label className="field-lbl">Advertiser ID</label>
                      <input className="input mono" style={{fontSize:12}} placeholder="e.g. abc123" value={draft.ttd_adv_id || ''} onChange={e=>{
                        set({ttd_adv_id: e.target.value});
                        if (e.target.value.trim()) pixelRequired.clear('pixel-ttd-advertiser');
                      }}/>
                      <div className="field-hint">{providerDefaults?.ttd_adv_id ? <>Organization default: <code>{providerDefaults.ttd_adv_id}</code></> : 'No organization default is configured for this ID.'}</div>
                    </RequiredField>
                    <RequiredField
                      fieldKey="pixel-ttd-conversion"
                      message={pixelRequired.messages['pixel-ttd-conversion']}
                      attempt={pixelRequired.attempt}
                      className="field"
                      style={{marginBottom:8}}
                    >
                      <label className="field-lbl">Conversion Tag ID</label>
                      <input className="input mono" style={{fontSize:12}} placeholder="0:xxx" value={draft.ttd_ct_id || ''} onChange={e=>{
                        set({ttd_ct_id: e.target.value});
                        if (!e.target.value || e.target.value.startsWith('0:')) pixelRequired.clear('pixel-ttd-conversion');
                      }}/>
                      <div className="field-hint">{providerDefaults?.ttd_ct_id ? <>Organization default: <code>{providerDefaults.ttd_ct_id}</code></> : 'No organization default is configured for this ID.'}</div>
                    </RequiredField>
                    <RequiredField
                      fieldKey="pixel-ttd-pageview"
                      message={pixelRequired.messages['pixel-ttd-pageview']}
                      attempt={pixelRequired.attempt}
                      className="field"
                    >
                      <label className="field-lbl">Pageview Conversion Tag ID</label>
                      <input className="input mono" style={{fontSize:12}} placeholder="0:pvw" value={draft.ttd_ct_id_pageview || ''} onChange={e=>{
                        set({ttd_ct_id_pageview: e.target.value});
                        if (!e.target.value || e.target.value.startsWith('0:')) pixelRequired.clear('pixel-ttd-pageview');
                      }}/>
                      <div className="field-hint">{providerDefaults?.ttd_ct_id_pageview ? <>Organization default: <code>{providerDefaults.ttd_ct_id_pageview}</code></> : 'No organization default is configured for this ID.'}</div>
                    </RequiredField>
                  </>
                )}
              </div>
            )}
            {!metaOn && !ttdOn && (
              <div className="asset-err" style={{marginTop:10}}>
                <span style={{flexShrink:0}}>⚠</span>
                <span style={{flex:1}}>At least one provider must be enabled.</span>
              </div>
            )}

            <div style={{height:1,background:'var(--line-2)',margin:'14px 0'}}/>

            {/* Server conversions (conversion_delivery — UI label "CAPI Forwarding") */}
            <div className="flex between items-center" style={{marginBottom:10}}>
              <div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:13,letterSpacing:'.02em'}}>Server conversions</div>
                <div className="muted" style={{fontSize:11}}>Batch-forwarded server events (Meta CAPI / TTD) — monitored under CAPI Forwarding</div>
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <div className="flex between items-center">
                <div style={{fontSize:12}}>Meta CAPI <span className="muted" style={{fontSize:10.5}}>· server events to this pixel's Meta Pixel ID</span></div>
                <button
                  className="btn btn-sm"
                  onClick={() => { if (!providerLocked) setConv({ meta_capi: !conv.meta_capi }); }}
                  disabled={providerLocked}
                  style={conv.meta_capi
                    ? {background:'var(--good)',color:'#fff',borderColor:'var(--good)',opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}
                    : {opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}}
                >{conv.meta_capi ? 'ON' : 'OFF'}</button>
              </div>
              <div className="flex between items-center">
                <div style={{fontSize:12}}>TTD offline conversions <span className="muted" style={{fontSize:10.5}}>· requires UID2 credentials server-side</span></div>
                <button
                  className="btn btn-sm"
                  onClick={() => { if (!providerLocked) setConv({ ttd: !conv.ttd }); }}
                  disabled={providerLocked}
                  style={conv.ttd
                    ? {background:'var(--good)',color:'#fff',borderColor:'var(--good)',opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}
                    : {opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}}
                >{conv.ttd ? 'ON' : 'OFF'}</button>
              </div>
              {providerLocked ? (
                (conv.test_event_code || null) && <LockedField label="Meta test event code" value={conv.test_event_code} placeholder="—"/>
              ) : (
                <RequiredField
                  fieldKey="pixel-test-event-code"
                  message={pixelRequired.messages['pixel-test-event-code']}
                  attempt={pixelRequired.attempt}
                  className="field"
                >
                  <label className="field-lbl">Meta test event code <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>drills only — clear after verifying</span></label>
                  <input className="input mono" style={{fontSize:12}} placeholder="TEST12345" maxLength={32}
                    value={conv.test_event_code || ''}
                    onChange={e=>{
                      setConv({ test_event_code: e.target.value.trim() || null });
                      if (e.target.value.length <= 32) pixelRequired.clear('pixel-test-event-code');
                    }}/>
                  <div className="field-hint">Routes server events to Meta's Test Events tab instead of production learning.</div>
                </RequiredField>
              )}
            </div>

            <div style={{height:1,background:'var(--line-2)',margin:'14px 0'}}/>

            {/* Server key (sGTM) — mint/rotate/revoke; the key is shown ONCE. */}
            <div className="flex between items-center" style={{marginBottom:8}}>
              <div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:13,letterSpacing:'.02em'}}>Server key (sGTM)</div>
                <div className="muted" style={{fontSize:11}}>Authorizes a GTM server container to POST trusted events</div>
              </div>
              {canManage && (
                <div style={{display:'inline-flex',gap:6}}>
                  <button
                    className="btn btn-sm"
                    onClick={onMintKey}
                    disabled={providerLocked || mintKeyM.isPending}
                    title={hasServerKey ? 'Rotate — mints a new key; the old one stops working within ~5 min' : 'Mint a server key for this pixel'}
                    style={{opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}}
                  >{mintKeyM.isPending ? '…' : (hasServerKey ? 'Rotate key' : 'Mint key')}</button>
                  {hasServerKey && (
                    <button
                      className="btn btn-sm"
                      onClick={onRevokeKey}
                      disabled={providerLocked || revokeKeyM.isPending}
                      title="Revoke — the server container stops being accepted within ~5 min"
                      style={{opacity: providerLocked ? .6 : 1, cursor: providerLocked ? 'not-allowed' : 'pointer'}}
                    >{revokeKeyM.isPending ? '…' : 'Revoke'}</button>
                  )}
                </div>
              )}
            </div>
            <div className="mono" style={{fontSize:11.5,color: hasServerKey ? 'var(--ink-1)' : 'var(--ink-3)'}}>
              {hasServerKey
                ? <>● Set{serverKeySetAt ? ` ${fmtKeyDate(serverKeySetAt)}` : ''} · rotation reaches the edge in ≤5 min</>
                : 'Not set'}
            </div>
            {(mintKeyM.error || revokeKeyM.error) && (
              <div className="field-hint" style={{marginTop:6,color:'var(--chart-1)'}}>Could not mint/revoke the server key. Check your permissions and try again.</div>
            )}
            </div>
          </RequiredField>

          <div className="field-lbl mb-2 flex between items-center" style={{paddingRight:2}}>
            <span>Funnel defaults</span>
            <LockToggleButton locked={funnelLocked} onClick={() => setFunnelLocked(l => !l)}/>
          </div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            {funnelLocked && (
              <div style={{
                display:'flex',alignItems:'center',gap:8,
                padding:'6px 10px',marginBottom:12,
                background:'var(--bg-sunk)',
                border:'1px solid var(--line)',
                borderRadius:'var(--r-sm,4px)',
                fontSize:11.5,color:'var(--ink-2)',
              }}>
                <span style={{color:'var(--ink-3)'}}><LockIcon/></span>
                <span>Funnel defaults are locked to prevent accidental edits. Click <b>Edit</b> above to change them.</span>
              </div>
            )}
            <div className="flex between items-center" style={{marginBottom: funnelOn ? 10 : 0}}>
              <div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:13,letterSpacing:'.02em'}}>Single production / venue funnel</div>
                <div className="muted" style={{fontSize:11.5,maxWidth:340}}>
                  {funnelOn
                    ? 'This pixel always fires for a specific venue and/or production. Both fields are optional.'
                    : 'Page-level data layer is expected to supply venue & production with each event.'}
                </div>
              </div>
              <button
                className="btn btn-sm"
                onClick={() => { if (!funnelLocked) toggleFunnel(); }}
                disabled={funnelLocked}
                style={funnelOn
                  ? {background:'var(--good)',color:'#fff',borderColor:'var(--good)',opacity: funnelLocked ? .6 : 1, cursor: funnelLocked ? 'not-allowed' : 'pointer'}
                  : {opacity: funnelLocked ? .6 : 1, cursor: funnelLocked ? 'not-allowed' : 'pointer'}}
              >{funnelOn ? 'ON' : 'OFF'}</button>
            </div>
            {funnelOn && (
              <div style={{marginTop:8}}>
                {funnelLocked ? (
                  <>
                    <LockedField label={<>Default venue ID <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>optional</span></>} value={draft.default_venue_id} placeholder="—"/>
                    <LockedField label={<>Default production ID <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>optional</span></>} value={draft.default_production_id} placeholder="—"/>
                  </>
                ) : (
                  <>
                    <RequiredField
                      fieldKey="pixel-default-venue"
                      message={pixelRequired.messages['pixel-default-venue']}
                      attempt={pixelRequired.attempt}
                      className="field"
                      style={{marginBottom:8}}
                    >
                      <label className="field-lbl">Default venue ID <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>optional</span></label>
                      <input className="input mono" style={{fontSize:12}} placeholder="Venue UUID" value={draft.default_venue_id || ''} onChange={e=>{
                        set({default_venue_id: e.target.value});
                        if (isValidEncoreId(e.target.value.trim(), 'Venue')) pixelRequired.clear('pixel-default-venue');
                      }}/>
                    </RequiredField>
                    <RequiredField
                      fieldKey="pixel-default-production"
                      message={pixelRequired.messages['pixel-default-production']}
                      attempt={pixelRequired.attempt}
                      className="field"
                    >
                      <label className="field-lbl">Default production <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>optional</span></label>
                      <SearchableDropdown
                        value={draft.default_production_id || ''}
                        options={productionOptions}
                        onChange={(v) => {
                          set({ default_production_id: v || null });
                          pixelRequired.clear('pixel-default-production');
                        }}
                        placeholder="— none · page-supplied —"
                        emptyOptionLabel="— none —"
                        searchPlaceholder="Search by name or house code…"
                      />
                      {productionsQ.isError && (
                        <div className="field-hint" style={{color:'var(--warn)'}}>Couldn't load the productions list — try reopening the drawer.</div>
                      )}
                    </RequiredField>
                    <div className="field-hint" style={{marginTop:8}}>
                      Leave both blank if the page's data layer will supply venue and production with every event.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Extraction · Identity/PII · Live test — edited in the full-width Studio. */}
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 12, paddingTop: 12 }}>
            <div className="field-lbl mb-2 flex between items-center">
              <span>Extraction · Identity · Live test</span>
              <button className="btn btn-sm btn-primary" onClick={() => setStudioOpen(true)}>Open Pixel Studio</button>
            </div>
            <div className="field-hint">
              {(draft.extraction?.rules?.length || 0)} field mapping{(draft.extraction?.rules?.length || 0) === 1 ? '' : 's'}
              {' · '}{(draft.extraction?.pii_rules?.length || 0)} PII rule{(draft.extraction?.pii_rules?.length || 0) === 1 ? '' : 's'}
              {' · '}Harvest PII {draft.pii?.enabled ? 'on' : 'off'}{draft.pii?.enabled && draft.pii?.advanced_matching ? ' + adv. matching' : ''}
              {' · '}auto-harvest {draft.extraction?.auto_harvest === false ? 'off' : 'on'}
            </div>
          </div>

          <div className="field-lbl mb-2 flex between items-center">
            <span>Install snippet</span>
            <div style={{display:'inline-flex',gap:6}}>
              {canManage && (
                <button className="btn btn-sm" onClick={onCopyLink} disabled={shareM.isPending}
                  title="Mint a no-login link to share this pixel's install & debug page with an agency or partner">
                  {shareM.isPending ? '…' : (linkCopied ? '✓ Link copied' : 'Copy share link')}
                </button>
              )}
              {canManage && shareInfo && (
                <button className="btn btn-sm" onClick={onRevoke} disabled={revokeM.isPending}
                  title="Invalidate every existing share link for this pixel">
                  {revokeM.isPending ? '…' : 'Revoke links'}
                </button>
              )}
            </div>
          </div>
          {shareInfo && (
            <div className="field-hint" style={{marginBottom:8,wordBreak:'break-all'}}>
              No-login link copied{shareInfo.expires_at ? <> · expires {fmtDate(shareInfo.expires_at)}</> : null}. Anyone with it can open this pixel's install &amp; debug page.
              <div className="mono" style={{color:'var(--ink-3)',marginTop:2}}>{shareInfo.url}</div>
            </div>
          )}
          {(shareM.error || revokeM.error) && (
            <div className="field-hint" style={{marginBottom:8,color:'var(--chart-1)'}}>Could not mint/revoke the share link. Check your permissions and try again.</div>
          )}
          <InstallSnippetBuilder pixel={snippetPixel}/>

          {/* Parameter reference — one catalog-driven table (same source as the builder) */}
          <div className="field-lbl mb-2 flex between items-center">
            <span>Parameter reference</span>
            <span className="muted mono" style={{fontSize:9.5,letterSpacing:'.08em',textTransform:'uppercase'}}>
              {INSTALL_PARAMS.filter(p => p.sam_key != null || p.data_attr != null || p.key === 'pixel_id').length} params
            </span>
          </div>
          <div style={{marginBottom:14}}>
            <ParamRefTable pixel={draft}/>
          </div>

          <div className="field-lbl mb-2">Fan-out</div>
          <div className="panel" style={{padding:12,marginBottom:14,borderLeft:'3px solid var(--accent)'}}>
            <div style={{fontSize:11.5,lineHeight:1.5,color:'var(--ink-1)'}}>
              When <code className="mono">units &gt; 1</code>, the Pixel Collector fires one pixel per ticket per platform.
              Example: <code className="mono">units=3</code>, <code className="mono">value=100.00</code> → three pixels each worth ~33.33.
              Per-ticket order IDs are <code className="mono">{'{ticket_id}-001'}</code>, <code className="mono">{'{ticket_id}-002'}</code>, <code className="mono">{'{ticket_id}-003'}</code>.
              Maximum fan-out: <b>{PIXEL_FANOUT_CAP} tickets</b>.
            </div>
          </div>

          <div className="field-lbl mb-2">Common mistakes</div>
          <div className="panel" style={{padding:'10px 12px 10px 28px',marginBottom:14}}>
            <ul style={{margin:0,padding:0,listStyle:'disc',fontSize:11.5,lineHeight:1.65,color:'var(--ink-1)'}}>
              {COMMON_MISTAKES.map((m, i) => <li key={i} style={{marginBottom: i === COMMON_MISTAKES.length-1 ? 0 : 3}}>{m}</li>)}
            </ul>
          </div>

          <div className="field-lbl mb-2">Activity <span className="muted" style={{textTransform:'none',letterSpacing:0}}>· from web-event ingest, trailing 7 days</span></div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            {/* Event-by-event activity — 7D / 24H / 1H / Last event per Pageview / Purchase, from GET /pixels/activity. */}
            {activityIssue ? (
              <div className="mono" style={{fontSize:11, color:'var(--warn)', padding:'4px 0'}}>
                ⚠ Activity unavailable — the insights tunnel didn’t answer. Counts resume automatically when it’s back.
              </div>
            ) : (() => {
              const rows = PIXEL_EVENTS.map(ev => {
                const r = activity?.[ev];
                return {
                  ev,
                  d7:  r?.count_7d ?? 0,
                  h24: r?.count_24h ?? 0,
                  h1:  r?.count_1h ?? 0,
                  last: r?.last_event_at ? fmtRelative(+new Date(r.last_event_at)) : '—',
                };
              });
              const totals = rows.reduce((t,r) => ({
                d7: t.d7 + r.d7, h24: t.h24 + r.h24, h1: t.h1 + r.h1,
              }), { d7:0, h24:0, h1:0 });
              const lastAny = lastAnyAt; // hoisted to drawer scope (also drives the Status line)
              const numCell: CSSProperties = { padding:'9px 0',borderBottom:'1px solid var(--line-2)',textAlign:'right',fontSize:12,fontVariantNumeric:'tabular-nums' };
              const headCell: CSSProperties = { fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',padding:'0 0 6px',borderBottom:'1px solid var(--line-2)',textAlign:'right' };
              return (
                <div className="stack-phone" style={{
                  display:'grid',
                  gridTemplateColumns:'1fr auto auto auto auto',
                  gap:'0 18px',
                  fontSize:12,
                }}>
                  <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',padding:'0 0 6px',borderBottom:'1px solid var(--line-2)'}}>Event</div>
                  <div className="muted mono" style={headCell}>7D</div>
                  <div className="muted mono" style={headCell}>24H</div>
                  <div className="muted mono" style={headCell}>1H</div>
                  <div className="muted mono" style={headCell}>Last event</div>
                  {rows.map(r => (
                    <Fragment key={r.ev}>
                      <div style={{padding:'9px 0',borderBottom:'1px solid var(--line-2)',display:'flex',alignItems:'center',gap:8}}>
                        <span style={{
                          display:'inline-block',width:6,height:6,borderRadius:'50%',
                          background: r.h24 > 0 ? 'var(--good)' : 'var(--ink-3)',
                          opacity: r.h24 > 0 ? 1 : .5,
                        }}/>
                        <span className="mono" style={{fontSize:11.5,fontWeight:600}}>{r.ev}</span>
                      </div>
                      <div className="mono num" style={numCell}>{r.d7 ? r.d7.toLocaleString() : <span className="muted">0</span>}</div>
                      <div className="mono num" style={numCell}>{r.h24 ? r.h24.toLocaleString() : <span className="muted">0</span>}</div>
                      <div className="mono num" style={numCell}>{r.h1 ? r.h1.toLocaleString() : <span className="muted">0</span>}</div>
                      <div className="mono" style={{...numCell, fontSize:11, color:'var(--ink-2)'}}>{r.last}</div>
                    </Fragment>
                  ))}
                  <div style={{padding:'9px 0',fontSize:12,fontWeight:700,fontFamily:'var(--font-display)',letterSpacing:'.02em'}}>Total</div>
                  <div className="mono num" style={{...numCell, borderBottom:'none', fontWeight:700}}>{totals.d7.toLocaleString()}</div>
                  <div className="mono num" style={{...numCell, borderBottom:'none', fontWeight:700}}>{totals.h24.toLocaleString()}</div>
                  <div className="mono num" style={{...numCell, borderBottom:'none', fontWeight:700}}>{totals.h1.toLocaleString()}</div>
                  <div className="mono" style={{...numCell, borderBottom:'none', fontSize:11, color:'var(--ink-2)'}}>{lastAny ? fmtRelative(+new Date(lastAny)) : '—'}</div>
                </div>
              );
            })()}
            <div style={{
              display:'flex',justifyContent:'space-between',alignItems:'center',
              marginTop:10,paddingTop:10,borderTop:'1px solid var(--line-2)',
              fontSize:11.5,
            }}>
              <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em',textTransform:'uppercase'}}>Status</span>
              <span>{statusLine()}</span>
            </div>
          </div>
        </div>

        {actionError && (
          <div style={{padding:'8px 16px', borderTop:'1px solid var(--line)'}}>
            <MutationErrorLine error={actionError}/>
          </div>
        )}
        <div className="drawer-foot">
          {canManage && dupOpen ? (
            <>
              <RequiredField
                fieldKey="duplicate-pixel-id"
                message={duplicateRequired.messages['duplicate-pixel-id']}
                attempt={duplicateRequired.attempt}
                style={{width:180, flexShrink:0}}
              >
                <input
                  className="input mono"
                  style={{fontSize:12, width:'100%'}}
                  autoFocus
                  placeholder="new pixel ID"
                  value={dupId}
                  onChange={e => {
                    const next = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g,'_');
                    setDupId(next);
                    if (next.length >= 3) duplicateRequired.clear('duplicate-pixel-id');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') submitDuplicate();
                    if (e.key === 'Escape') {
                      setDupOpen(false);
                      setDupId('');
                      duplicateRequired.clearAll();
                    }
                  }}
                />
              </RequiredField>
              <button className="btn btn-sm btn-primary" disabled={busy} onClick={submitDuplicate}>
                {busy ? 'Duplicating…' : 'Create copy'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => {
                setDupOpen(false);
                setDupId('');
                duplicateRequired.clearAll();
              }}>Cancel</button>
            </>
          ) : canManage ? (
            <>
              <button className="btn btn-sm" disabled={busy} onClick={() => {
                duplicateRequired.clearAll();
                setDupOpen(true);
              }}>Duplicate</button>
              <button className="btn btn-sm" disabled={busy} onClick={() => onArchive?.(pixel)}>
                {pixel.status === 'active' ? 'Archive' : 'Unarchive'}
              </button>
            </>
          ) : (
            <span className="muted mono" style={{fontSize:10, letterSpacing:'.06em'}}>Read-only · pixels_manage required to edit</span>
          )}
          {canManage && (
            <button className="btn btn-primary" style={{marginLeft:'auto'}} disabled={busy} onClick={() => attemptSave(false)}>
              {Icons.check} {busy ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </ResponsiveOverlay>
      <PixelStudio
        open={studioOpen}
        pixel={pixel}
        draft={draft}
        set={set}
        canManage={canManage}
        saving={busy}
        onClose={() => setStudioOpen(false)}
        onSave={onSave}
        onAttemptSave={() => attemptSave(true)}
        requiredFeedback={pixelRequired}
      />
      <ServerKeyModal serverKey={mintedKey} pixelId={pixel.id} onClose={() => setMintedKey(null)}/>
    </>
  );
}

// Show-once server-key modal — the mint response is the ONLY time the key is
// visible (the registry stores a hash). Same modal idiom as NewPixelModal.
interface ServerKeyModalProps {
  serverKey: string | null;
  pixelId: string;
  onClose: () => void;
}

function ServerKeyModal({ serverKey, pixelId, onClose }: ServerKeyModalProps) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [serverKey]);
  if (!serverKey) return null;
  const copy = () => {
    navigator.clipboard?.writeText(serverKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };
  return (
    <ResponsiveOverlay label="pixels" kind="form" onClose={onClose} className="modal-host">
      <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:560}}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">Server key minted</div>
            <div className="muted" style={{fontSize:11.5,marginTop:2}}>Pixel <code className="mono">{pixelId}</code> · any previous key stops working within ~5 min</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="mono" style={{
            display:'flex',alignItems:'center',gap:8,
            background:'var(--bg-sunk)',border:'1px solid var(--line)',
            borderRadius:'var(--r-sm,4px)',padding:'10px 12px',
            fontSize:12,wordBreak:'break-all',
          }}>
            <span style={{flex:1,minWidth:0}}>{serverKey}</span>
            <button className="btn btn-sm" style={{flexShrink:0}} onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
          </div>
          <div style={{
            display:'flex',gap:8,alignItems:'flex-start',
            marginTop:12,padding:'8px 10px',
            background:'color-mix(in oklch, var(--warn) 10%, transparent)',
            border:'1px solid color-mix(in oklch, var(--warn) 35%, transparent)',
            borderRadius:'var(--r-sm,4px)',fontSize:11.5,lineHeight:1.5,
          }}>
            <span style={{flexShrink:0}}>⚠</span>
            <span>This key is <b>shown once</b> — store it in your GTM <b>server container</b> as a constant (the <code className="mono">Server key</code> template field). It cannot be retrieved later; if lost, rotate to mint a new one.</span>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}
